// src/utils/utils_creatorCapacityPurchase.ts
//
// Logique commune aux 5 ressources d'achat de quota créateur (disque, devices, chaînes,
// durée de diffusion, durée d'enregistrement) — extraite ici parce qu'elle a cessé
// d'être trois lignes similaires le jour où chaque ressource a reçu plusieurs paliers de
// prix (cf. claude/achats-quotas-createur-2026-09-07.md) : choisir un palier, ajouter au
// panier, ouvrir directement le paiement (QuotaPurchaseModal). Les fichiers
// utils_*Purchase.ts restent le point d'entrée par ressource (label, message d'absence de
// produit) ; ne changer qu'ici la mécanique elle-même.

import { store              }  from "../store/store";

import { getProducts        }  from "../backendServices/productManagment/api_product";
import { getCart            ,
         addItemToCart       }  from "../backendServices/purchaseManagment/api";
import { openPurchaseModal  }  from "../store/purchaseModalManagment/actions";
import { openSubscriptionPurchaseModal }  from "../store/subscriptionPurchaseModalManagment/actions";
import { pickTier            ,
         PurchaseTier         }  from "./utils_tierPicker";

export async function buyCreatorCapacity(
  associatedAdminResource: string,
  unavailableMessage: string,
  pickerTitle: string,
  notify: (message: string) => void = (message) => window.alert(message)
): Promise<void> {

  const email = store.getState().authenticationReducer.authentication.admin?.email;

  if (!email) {
    notify("Impossible d'identifier le compte créateur.");
    return;
  }

  const productsResponse = await getProducts();

  if (productsResponse.code !== 1) {
    notify(productsResponse.comment || "Impossible de récupérer le catalogue.");
    return;
  }

  const product = productsResponse.payload.find(
    (p) => p.kind === 'CreatorCapacity' && p.associatedAdminResource === associatedAdminResource
  );

  // Un palier retiré du catalogue (remplacé par une nouvelle grille de prix, cf. section 7
  // de la note) reste en base — référencé par les achats passés — mais désactivé : il ne
  // doit plus être proposé ici.
  const activeVariants = (product?.variants ?? []).filter(
    (v) => v.active && v.code && v.prices[0]?.code
  );

  if (!product || activeVariants.length === 0) {
    notify(unavailableMessage);
    return;
  }

  let variantCode  : string;
  let priceCode    : string;
  let purchaseLabel: string;
  let isRecurring  : boolean;
  let priceWithTax : number;  // euros, TTC — cf. PurchaseTier.priceWithTax

  if (activeVariants.length === 1) {
    variantCode   = activeVariants[0].code as string;
    priceCode     = activeVariants[0].prices[0].code as string;
    purchaseLabel = product.name;
    isRecurring   = !!activeVariants[0].recurringIntervalUnit;
    priceWithTax  = activeVariants[0].prices[0].priceWithTax;
  } else {
    const tiers: PurchaseTier[] = activeVariants.map((v) => ({
      variantCode :            v.code as string,
      priceCode   :            v.prices[0].code as string,
      name        :            v.name || (v.code as string),
      priceWithTax:            v.prices[0].priceWithTax,
      isRecurring :            !!v.recurringIntervalUnit,
      recurringIntervalUnit:   v.recurringIntervalUnit ?? null,
    }));

    const picked = await pickTier(pickerTitle, tiers);

    if (!picked) {
      return; // annulé par l'utilisateur, rien à notifier
    }

    variantCode   = picked.variantCode;
    priceCode     = picked.priceCode;
    purchaseLabel = picked.name;
    isRecurring   = picked.isRecurring;
    priceWithTax  = picked.priceWithTax;
  }

  // Un abonnement n'a pas de panier : la ressource et son prix viennent directement du
  // catalogue (cf. SubscriptionCreation.php) — pas d'addItemToCart/getCart ici.
  if (isRecurring) {
    store.dispatch(openSubscriptionPurchaseModal(variantCode, priceCode, Math.round(priceWithTax * 100), purchaseLabel));
    return;
  }

  const cartResponse = await getCart(email);

  if (cartResponse.code !== 1) {
    notify(cartResponse.comment || "Impossible de récupérer le panier.");
    return;
  }

  const addResponse = await addItemToCart(cartResponse.payload.id, variantCode, priceCode, 1);

  if (addResponse.code !== 1) {
    notify(addResponse.comment || "Impossible d'ajouter ce produit au panier.");
    return;
  }

  // Le montant à payer est celui du panier une fois l'article ajouté (pas seulement le
  // prix de cet article) : un panier laissé non vide par une tentative précédente doit
  // être payé en entier, pas ignoré.
  const refreshedCart = await getCart(email);

  if (refreshedCart.code !== 1) {
    notify(refreshedCart.comment || "Impossible de récupérer le panier.");
    return;
  }

  store.dispatch(openPurchaseModal(refreshedCart.payload.id, refreshedCart.payload.amount, purchaseLabel));
}
